import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter
} from 'firebase/firestore';
import { firestore } from './firebase/firebase';
import { functions } from './firebase/firebase';
import { httpsCallable } from 'firebase/functions';

const USERS_COLLECTION = 'users';

/**
 * Récupère une liste paginée d'utilisateurs
 */
export const getUsers = async (
  pageSize = 10, 
  lastVisible = null, 
  orderByField = 'createdAt', 
  searchTerm = ''
) => {
  try {
    console.log('Tentative de récupération des utilisateurs depuis la collection:', USERS_COLLECTION);
    let usersQuery;
    
    if (searchTerm) {
      // Recherche par email, prénom ou nom
      usersQuery = query(
        collection(firestore, USERS_COLLECTION),
        where('email', '>=', searchTerm),
        where('email', '<=', searchTerm + '\uf8ff'),
        orderBy('email'),
        limit(pageSize)
      );
    } else {
      // Liste paginée standard
      if (lastVisible) {
        usersQuery = query(
          collection(firestore, USERS_COLLECTION),
          orderBy(orderByField),
          startAfter(lastVisible),
          limit(pageSize)
        );
      } else {
        usersQuery = query(
          collection(firestore, USERS_COLLECTION),
          orderBy(orderByField),
          limit(pageSize)
        );
      }
    }
    
    const snapshot = await getDocs(usersQuery);
    console.log(`Nombre d'utilisateurs récupérés: ${snapshot.size}`);
    
    // Adapter le mappage des données selon la structure de MSD Portail
    const users = snapshot.docs.map(doc => {
      const data = doc.data();
      
      // Mapper les champs selon la structure du document MSD Portail
      return {
        id: doc.id,
        uid: data.uid || '',
        msdId: data.msdId || '',
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        phone: data.phone || '',
        username: data.username || '',
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        
        // Informations d'entreprise
        isCompany: data.isCompany || false,
        companyName: data.companyName || '',
        siretNumber: data.siretNumber || '',
        vatNumber: data.vatNumber || '',
        frLegalType: data.frLegalType || '',
        
        // Adresse
        address1: data.address1 || '',
        city: data.city || '',
        postalCode: data.postalCode || '',
        state: data.state || '',
        country: data.country || '',
        
        // Informations de naissance
        birthDate: data.birthDate || '',
        birthCity: data.birthCity || '',
        birthPostalCode: data.birthPostalCode || '',
        birthCountry: data.birthCountry || '',
        
        // Informations d'abonnement
        subscriptionPlan: data.subscriptionPlan || '',
        subscriptionStatus: data.subscriptionStatus || '',
        subscriptionExpiryDate: data.subscriptionExpiryDate,
        subscriptionId: data.subscriptionId || '',
        subscriptionUpdatedAt: data.subscriptionUpdatedAt,
        stripeCustomerId: data.stripeCustomerId || '',
        
        // Informations d'emails
        welcomeEmailSent: data.welcomeEmailSent || false,
        welcomeEmailTimestamp: data.welcomeEmailTimestamp,
        
        // État par défaut (pour la compatibilité avec notre interface d'administration)
        status: data.subscriptionStatus === 'active' ? 'active' : 'disabled',
        role: data.isCompany ? 'business' : 'personal'
      };
    });
    
    const newLastVisible = snapshot.docs.length > 0
      ? snapshot.docs[snapshot.docs.length - 1]
      : null;
    
    return { users, lastVisible: newLastVisible };
  } catch (error) {
    console.error('Erreur lors de la récupération des utilisateurs:', error);
    return { users: [], lastVisible: null };
  }
};

/**
 * Récupère les détails d'un utilisateur par son ID
 */
export const getUserById = async (userId) => {
  try {
    const userDoc = await getDoc(doc(firestore, USERS_COLLECTION, userId));
    
    if (userDoc.exists()) {
      const data = userDoc.data();
      return {
        id: userDoc.id,
        uid: data.uid || '',
        msdId: data.msdId || '',
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        phone: data.phone || '',
        username: data.username || '',
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        
        // Informations d'entreprise
        isCompany: data.isCompany || false,
        companyName: data.companyName || '',
        siretNumber: data.siretNumber || '',
        vatNumber: data.vatNumber || '',
        frLegalType: data.frLegalType || '',
        
        // Adresse
        address1: data.address1 || '',
        city: data.city || '',
        postalCode: data.postalCode || '',
        state: data.state || '',
        country: data.country || '',
        
        // Informations de naissance
        birthDate: data.birthDate || '',
        birthCity: data.birthCity || '',
        birthPostalCode: data.birthPostalCode || '',
        birthCountry: data.birthCountry || '',
        
        // Informations d'abonnement
        subscriptionPlan: data.subscriptionPlan || '',
        subscriptionStatus: data.subscriptionStatus || '',
        subscriptionExpiryDate: data.subscriptionExpiryDate,
        subscriptionId: data.subscriptionId || '',
        subscriptionUpdatedAt: data.subscriptionUpdatedAt,
        stripeCustomerId: data.stripeCustomerId || '',
        
        // Informations d'emails
        welcomeEmailSent: data.welcomeEmailSent || false,
        welcomeEmailTimestamp: data.welcomeEmailTimestamp,
        
        // État par défaut (pour la compatibilité avec notre interface d'administration)
        status: data.subscriptionStatus === 'active' ? 'active' : 'disabled',
        role: data.isCompany ? 'business' : 'personal'
      };
    } else {
      throw new Error('Utilisateur non trouvé');
    }
  } catch (error) {
    console.error(`Erreur lors de la récupération de l'utilisateur ${userId}:`, error);
    throw error;
  }
};

/**
 * Crée un nouvel utilisateur
 * @param {Object} userData - Données de l'utilisateur
 * @returns {Promise<string>} - ID du nouvel utilisateur
 */
export const createUser = async (userData) => {
  try {
    const timestamp = new Date();
    const userRef = await addDoc(collection(firestore, USERS_COLLECTION), {
      ...userData,
      createdAt: timestamp,
      updatedAt: timestamp,
      status: userData.status || 'active'
    });
    
    return userRef.id;
  } catch (error) {
    console.error('Erreur lors de la création de l\'utilisateur:', error);
    throw error;
  }
};

/**
 * Met à jour les informations d'un utilisateur
 * @param {string} userId - ID de l'utilisateur
 * @param {Object} userData - Données à mettre à jour
 * @returns {Promise<void>}
 */
export const updateUser = async (userId, userData) => {
  try {
    const userRef = doc(firestore, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      ...userData,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error(`Erreur lors de la mise à jour de l'utilisateur ${userId}:`, error);
    throw error;
  }
};

/**
 * Change le statut d'un utilisateur (activer/désactiver)
 * @param {string} userId - ID de l'utilisateur
 * @param {string} status - Nouveau statut ('active', 'disabled', etc.)
 * @returns {Promise<void>}
 */
export const changeUserStatus = async (userId, status) => {
  try {
    const userRef = doc(firestore, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      status,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error(`Erreur lors du changement de statut de l'utilisateur ${userId}:`, error);
    throw error;
  }
};

/**
 * Supprime un utilisateur (suppression COMPLÈTE : Auth + Firestore + site via onCall).
 * @param {string} userId - ID de l'utilisateur (uid auth)
 * @returns {Promise<Object>} Résultat { success, deleted: {auth,user,site,feedback} }
 */
export const deleteUser = async (userId) => {
  try {
    const fn = httpsCallable(functions, 'deleteUserComplete');
    const result = await fn({ uid: userId });
    return result.data;
  } catch (error) {
    console.error(`Erreur lors de la suppression complète de l'utilisateur ${userId}:`, error);
    throw error;
  }
};

/**
 * Récupère le nombre total d'utilisateurs
 */
export const getUserCount = async () => {
  try {
    const snapshot = await getDocs(collection(firestore, USERS_COLLECTION));
    return snapshot.size;
  } catch (error) {
    console.error('Erreur lors du comptage des utilisateurs:', error);
    return 0;
  }
};